import { createContext, ReactNode, useContext, useMemo } from "react"

import {
    AudioDeviceManager,
    AudioSourceInfo,
    CameraManager,
    VideoSourceInfo,
} from "modules/Call/sdk"
import { useInputDevice } from "modules/Call/InputDevices/hooks/useInputDevice"
import { VoximplantInputDevice } from "modules/Call/InputDevices/VoximplantInputDevice"
import { SelectedDevicesStorage } from "modules/Call/InputDevices/SelectedDevicesStorage"
import { useDidMount } from "utils/customHooks"
import { useGetOwner } from "modules/Call/routing"

export interface InputDevicesContextValues {
    cameraError?: Error
    microphoneError?: Error
    cameraLoading: boolean
    microphoneLoading: boolean
    cameras: VideoSourceInfo[]
    microphones: AudioSourceInfo[]
    camera?: VideoSourceInfo
    microphone?: AudioSourceInfo
    cameraAccessed: boolean
    microphoneAccessed: boolean
    cameraEnabled: boolean
    microphoneEnabled: boolean
}

interface ContextHandlers {
    selectCamera: (id: VideoSourceInfo["id"]) => void
    selectMicrophone: (id: AudioSourceInfo["id"]) => void
    enableCamera: (value: boolean) => void
    enableMicrophone: (value: boolean) => void
}

const InputDevicesContext = createContext<[InputDevicesContextValues, ContextHandlers] | null>(null)

interface Props {
    children: ReactNode
}
export function InputDevicesProvider(props: Props) {
    const owner = useGetOwner(true)

    const cameraDevice = useMemo(
        () =>
            new VoximplantInputDevice(
                CameraManager,
                new SelectedDevicesStorage(`user-device-camera-${owner}`)
            ),
        [owner]
    )
    const microphoneDevice = useMemo(
        () =>
            new VoximplantInputDevice(
                AudioDeviceManager,
                new SelectedDevicesStorage(`user-device-microphone-${owner}`)
            ),
        [owner]
    )

    const [
        {
            list: cameras,
            selected: camera,
            error: cameraError,
            loading: cameraLoading,
            accessed: cameraAccessed,
            enabled: cameraEnabled,
        },
        { init: initCamera, selectDevice: selectCamera, enable: enableCamera },
    ] = useInputDevice(cameraDevice)
    const [
        {
            list: microphones,
            selected: microphone,
            error: microphoneError,
            loading: microphoneLoading,
            accessed: microphoneAccessed,
            enabled: microphoneEnabled,
        },
        { init: initMicrophone, selectDevice: selectMicrophone, enable: enableMicrophone },
    ] = useInputDevice(microphoneDevice)

    useDidMount(() => {
        async function request() {
            await initMicrophone()
            await initCamera()
        }

        void request()
    })

    return (
        <InputDevicesContext.Provider
            value={[
                {
                    cameraError,
                    microphoneError,
                    cameraLoading,
                    microphoneLoading,
                    cameras,
                    microphones,
                    camera,
                    microphone,
                    cameraAccessed,
                    microphoneAccessed,
                    cameraEnabled,
                    microphoneEnabled,
                },
                {
                    selectCamera,
                    selectMicrophone,
                    enableCamera,
                    enableMicrophone,
                },
            ]}
        >
            {props.children}
        </InputDevicesContext.Provider>
    )
}
export function useInputDevicesContext() {
    const currentContext = useContext(InputDevicesContext)
    if (!currentContext) {
        throw new Error("useInputDevicesContext has to be used within <InputDevicesProvider>")
    }

    return currentContext
}
